import { Trans } from 'react-i18next'
import { Link } from 'react-router-dom'

import { TeamMemberRole } from '@makyoapp/makyo-sdk/models/ACL'
import { TeamItemAdapter } from '@makyoapp/makyo-common'
import { Plus16 } from '@makyoapp/makyo-icon/icon'
import { AvatarCell, Button, Checkbox, JustifyVariant, Modal, Table, Td, Th, Tr } from '@makyoapp/makyo-ui'

import { ORG_MEMBER_ROUTE, ORG_ROUTE } from '@/shared/constants'
import { RoleDropdownTd } from '@/components/RoleDropdown'
import { SaveButton } from '@/components/SaveButton'

import useAddTeamMemberModal from '../hooks/useAddTeamMemberModal'

interface AddTeamMemberModalProps {
  team: TeamItemAdapter
}

function AddTeamMemberModal({ team }: AddTeamMemberModalProps) {
  const {
    showModal,
    resetModal,
    setShowModal,
    memberOptions,
    addNewMembers,
    handleCheck,
    handleCheckAll,
    handleAddMemberRole,
    currentOrgId,
    isBtnLoading,
    handleSaveMember,
    t,
  } = useAddTeamMemberModal(team)

  // Filter out org owners from memberOptions
  const filteredMemberOptions = memberOptions.filter((member) => member.orgRole !== 'Owner')

  return (
    <>
      {/* Button to show Add Team Member modal */}
      <Button id='add-team-member-btn' icon={<Plus16 />} onClick={() => setShowModal(true)}>
        {t('Add new member')}
      </Button>
      {/* Render - Add Team Member Modal */}
      <Modal id='add-team-member-modal' show={showModal} onHide={resetModal}>
        <Modal.Header onCloseClick={resetModal}>{t('Add new members')}</Modal.Header>
        <Modal.Body>
          <div className='modal-md'>
            {filteredMemberOptions.length > 0 ? (
              <Table defaultOverflow={false} className='border bdr'>
                <thead>
                  <Tr>
                    <Th className='chk'>
                      {/* Check All Member */}
                      <Checkbox
                        id='add-team-member-all-chk'
                        checked={memberOptions.length === addNewMembers.length}
                        onClick={handleCheckAll}
                      />
                    </Th>
                    <Th>{t('Member')}</Th>
                    <Th>{t('Role')}</Th>
                  </Tr>
                </thead>
                <tbody>
                  {filteredMemberOptions.map((member, idx) => {
                    const checked = addNewMembers.filter((m) => m.member.id === member.id).length > 0

                    const avatarUrl = member.avatar ? `${member.avatar}?t=${Date.now()}` : member.avatar
                    return (
                      <Tr key={member.id}>
                        <Td className='chk'>
                          {/* Check One Member */}
                          <Checkbox
                            id={`add-team-member-${idx}-chk`}
                            className='add-team-member-chk'
                            checked={checked}
                            onClick={(isChecked) => handleCheck(isChecked, member)}
                          />
                        </Td>
                        <Td>
                          <AvatarCell {...member} avatar={avatarUrl} />
                        </Td>
                        {/* Render Dropdown Member Roles */}
                        <RoleDropdownTd
                          id={`add-team-member-${idx}-role-dd`}
                          className='add-team-member-role-dd'
                          disabled={!checked}
                          role={TeamMemberRole.Viewer}
                          isTeam={true}
                          onChange={(opt) => handleAddMemberRole(opt, member)}
                        />
                      </Tr>
                    )
                  })}
                </tbody>
              </Table>
            ) : (
              <Trans i18nKey='addTeamWithOrgMemberEmpty'>
                This organization does not have any additional members. Please add them on the{' '}
                <Link className='link' to={`${ORG_ROUTE}/${currentOrgId}/${ORG_MEMBER_ROUTE}`}>
                  members page
                </Link>{' '}
                first.
              </Trans>
            )}
          </div>
        </Modal.Body>
        {/* Validation: if team object is exist and member options is not Empty */}
        {team && filteredMemberOptions.length > 0 && (
          <Modal.Footer justify={JustifyVariant.End}>
            {/* Render - Save Member */}
            <SaveButton
              id='add-team-member-submit'
              disabled={addNewMembers.length < 1}
              isLoading={isBtnLoading}
              onClick={handleSaveMember}
              label={t('Add selected members')}
            />
          </Modal.Footer>
        )}
      </Modal>
    </>
  )
}

export default AddTeamMemberModal
